import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { SaveTestKit, UpdateTestKit } from "../../../services/TestKitsService";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../../common/CommonLoader";

const AddTestKit = ({ open, onClose, onSave, selectedKit }) => {
  const [kitName, setKitName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [expectedTurnAroundTime, setExpectedTurnAroundTime] = useState("10");
  const [price, setPrice] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [createdBy, setCreatedBy] = useState("");
  const [modifiedBy, setModifiedBy] = useState("");
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedKit) {
      setKitName(selectedKit.kitName || "");
      setDescription(selectedKit.description || "");
      setImage(selectedKit.image || "");
      setExpectedTurnAroundTime(selectedKit.expectedTurnAroundTime || "10");
      setPrice(selectedKit.price || "");
      setIsActive(
        selectedKit.isActive !== undefined ? selectedKit.isActive : true
      );
      setModifiedBy(selectedKit.modifiedBy || "");
    } else {
      resetForm();
    }
  }, [selectedKit]);

  const resetForm = () => {
    setKitName("");
    setDescription("");
    setImage("");
    setExpectedTurnAroundTime("10");
    setPrice("");
    setIsActive(true);
    setCreatedBy("");
    setModifiedBy("");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setErrors({
          ...errors,
          image: "Please upload a valid image file (JPEG, PNG, GIF, or WebP)",
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({
          ...errors,
          image: "Image size must be less than 5MB",
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        setErrors({ ...errors, image: "" });
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required field checks
    if (!kitName) newErrors.kitName = "Kit Name is required";
    if (!description) newErrors.description = "Description is required";
    if (!image) newErrors.image = "Image is required";
    if (!expectedTurnAroundTime)
      newErrors.expectedTurnAroundTime =
        "Expected Turn Around Time is required";
    if (!price) newErrors.price = "Price is required";

    // Price validation
    if (price && (isNaN(price) || parseFloat(price) <= 0)) {
      newErrors.price = "Price must be a positive number";
    }

    // Turn Around Time validation
    if (
      expectedTurnAroundTime &&
      (isNaN(expectedTurnAroundTime) || parseInt(expectedTurnAroundTime) <= 0)
    ) {
      newErrors.expectedTurnAroundTime =
        "Turn Around Time must be a positive number";
    }

    // Kit Name length validation
    if (kitName && kitName.length > 200) {
      newErrors.kitName = "Kit Name must be maximum 200 characters";
    }

    // Description length validation
    if (description && description.length > 1000) {
      newErrors.description = "Description must be maximum 1000 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const kitData = {
        testKitId: selectedKit?.testKitId || 0,
        kitName,
        description,
        image,
        expectedTurnAroundTime: expectedTurnAroundTime.toString(),
        price: parseFloat(price),
        isActive,
        isDeleted: selectedKit?.isDeleted || false,
        createdBy: selectedKit ? selectedKit.createdBy : createdBy,
        modifiedBy: selectedKit ? modifiedBy : null,
      };

      let response;
      if (selectedKit) {
        setLoading(true);
        response = await UpdateTestKit(kitData);
        NotificationManager.success("Test Kit updated successfully");
      } else {
        setLoading(true);
        response = await SaveTestKit(kitData);
        NotificationManager.success("Test Kit saved successfully");
      }
      setLoading(false);
      if (response?.status === 200) {
        onSave();
        onClose();
      } else {
        NotificationManager.error("Error saving Test Kit");
      }
    } catch (error) {
      NotificationManager.error("Error saving Test Kit");
    }
  };

  const handleCancel = () => onClose();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedKit ? "Update Test Kit" : "Add New Test Kit"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Kit Name"
                value={kitName}
                onChange={(e) => setKitName(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                error={!!errors.kitName}
                helperText={errors.kitName}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                error={!!errors.price}
                helperText={errors.price}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected Turn Around Time (days)"
                type="number"
                value={expectedTurnAroundTime}
                onChange={(e) => setExpectedTurnAroundTime(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                error={!!errors.expectedTurnAroundTime}
                helperText={errors.expectedTurnAroundTime}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
              >
                <TextField
                  select
                  label="Status"
                  value={isActive}
                  size="small"
                  onChange={(e) => setIsActive(e.target.value === "true")}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </TextField>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.image}>
                <Button
                  variant="outlined"
                  component="label"
                  style={{ marginTop: "8px" }}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {image && (
                  <div style={{ marginTop: "16px", textAlign: "center" }}>
                    <img
                      src={image}
                      alt="Preview"
                      style={{ maxWidth: "100%", maxHeight: "200px" }}
                    />
                  </div>
                )}
                <FormHelperText>{errors.image}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedKit ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddTestKit;
